import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import apiService from '../../services/api'
import { localDateTimeToISO, toLocalDateInput, toLocalTimeInput } from '../../utils/localDate'
import {
  HiOutlineArrowLeft,
  HiOutlineVideoCamera,
  HiOutlineUser,
  HiOutlineCalendarDays,
  HiOutlineClock,
  HiOutlineFolder,
  HiOutlineCheckCircle,
} from 'react-icons/hi2'
import {
  Banner,
  Button,
  Card,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Select,
  Textarea,
} from '../../components/ui'

function extractClientId(selectedCase) {
  if (!selectedCase) return null
  if (selectedCase.client && typeof selectedCase.client === 'object') return selectedCase.client.id
  if (selectedCase.client_id) return selectedCase.client_id
  if (selectedCase.clientId) return selectedCase.clientId
  if (typeof selectedCase.client === 'number' || typeof selectedCase.client === 'string') return selectedCase.client
  return null
}

export default function CreateSessionPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { getEntity } = useWorkspace()
  const { canCreate } = usePermissionCheck()

  useEffect(() => {
    if (!canCreate('session')) {
      showError(t('common.permissionDenied'))
      navigate(-1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreate])

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    case: searchParams.get('case') || '',
    client: '',
    date: toLocalDateInput(new Date()),
    time: toLocalTimeInput(new Date()),
  })
  const [cases, setCases] = useState([])
  const [clients, setClients] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [casesLoading, setCasesLoading] = useState(true)
  const [clientsLoading, setClientsLoading] = useState(true)

  // Load cases + clients in parallel
  useEffect(() => {
    let cancelled = false
    const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
    ;(async () => {
      try {
        // page=all: list endpoints return 10 items per page by default, which left
        // older entries out of this dropdown.
        const response = await apiService.get('/cases/', { params: { ...params, page: 'all' } })
        if (!cancelled && response.success) {
          const data = response.data.data || response.data
          setCases(data.data || data.results || [])
        }
      } catch { /* swallow */ } finally {
        if (!cancelled) setCasesLoading(false)
      }
    })()
    ;(async () => {
      try {
        const response = await apiService.get('/clients/', { params: { ...params, page: 'all' } })
        if (!cancelled && response.success) {
          const data = response.data.data || response.data
          setClients(data.data || data.results || [])
        }
      } catch { /* swallow */ } finally {
        if (!cancelled) setClientsLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [ownerAccountId, isWorkspaceSwitched])

  // Auto-fill client when cases load + a case is pre-selected
  useEffect(() => {
    if (cases.length > 0 && formData.case && !formData.client) {
      const selectedCase = cases.find(c => c.id === parseInt(formData.case))
      const clientId = extractClientId(selectedCase)
      if (clientId) setFormData(prev => ({ ...prev, client: clientId.toString() }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cases, formData.case])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))

    // Case→client cascade
    if (name === 'case' && value) {
      const selectedCase = cases.find(c => c.id === parseInt(value))
      const clientId = extractClientId(selectedCase)
      if (clientId) {
        setFormData(prev => ({ ...prev, client: clientId.toString() }))
        if (errors.client) setErrors(prev => ({ ...prev, client: '' }))
      }
    }
  }

  const validate = () => {
    const next = {}
    if (!formData.title.trim()) next.title = t('cases.create.validationTitleRequired', { entity: getEntity('session').singular })
    if (!formData.case) next.case = t('sessions.create.validationCaseFirst', { caseEntity: getEntity('case').singular.toLowerCase(), clientEntity: getEntity('client').singular.toLowerCase() })
    if (!formData.client) next.client = formData.case
      ? t('sessions.create.validationClientUnfilled', { clientEntity: getEntity('client').singular })
      : t('sessions.create.validationCaseFirst', { caseEntity: getEntity('case').singular.toLowerCase(), clientEntity: getEntity('client').singular.toLowerCase() })
    if (!formData.date) next.date = t('cases.create.validationDateRequired', { entity: getEntity('session').singular })
    if (!formData.time) next.time = t('cases.create.validationDateRequired', { entity: getEntity('session').singular })
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    try {
      setLoading(true)
      // Send an explicit UTC instant. A bare "2026-10-20T20:30:00" is read
      // by the server as UTC, which moved sessions by the user's offset.
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        case_id: parseInt(formData.case),
        client_id: formData.client ? parseInt(formData.client) : null,
        date: localDateTimeToISO(formData.date, formData.time),
      }
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.post('/sessions/', payload, { params })
      if (response.success) {
        showSuccess(t('sessions.create.successMessage', { entity: getEntity('session').singular }))
        navigate('/sessions')
      } else if (response.error?.data) {
        setErrors(response.error.data)
      } else {
        showError(response.error?.message || t('cases.deleteFailed', { entity: getEntity('session').singular.toLowerCase() }))
      }
    } catch {
      showError(t('cases.deleteFailed', { entity: getEntity('session').singular.toLowerCase() }))
    } finally {
      setLoading(false)
    }
  }

  const selectedCase = cases.find(c => c.id === parseInt(formData.case))
  const subtitle = isWorkspaceSwitched
    ? t('sessions.create.subtitleSwitched', { entity: getEntity('session').singular.toLowerCase(), name: user?.switchedAccountName })
    : selectedCase
      ? t('sessions.create.subtitleWithCase', { entity: getEntity('session').singular.toLowerCase(), caseEntity: getEntity('case').singular.toLowerCase(), caseTitle: selectedCase.title })
      : t('sessions.create.subtitleDefault', { entity: getEntity('session').singular.toLowerCase(), caseEntity: getEntity('case').singular.toLowerCase() })

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <IconButton
          icon={HiOutlineArrowLeft}
          aria-label={t('sessions.detail.backTo', { entity: getEntity('session').plural })}
          variant="ghost"
          onClick={() => {
            const caseId = searchParams.get('case')
            navigate(caseId ? `/cases/${caseId}` : '/sessions')
          }}
          className="rtl:rotate-180"
        />
        <PageHeader
          icon={HiOutlineVideoCamera}
          iconTone="brand"
          title={t('sessions.create.title', { entity: getEntity('session').singular.toLowerCase() })}
          subtitle={subtitle}
          className="!mb-0 flex-1"
        />
      </div>

      <Card padding="none">
        <Card.Header className="px-5 sm:px-6 py-4 mb-0 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <HiOutlineVideoCamera className="w-4 h-4 text-ink-muted" />
            <h2 className="text-sm font-semibold text-ink-primary">
              {t('cases.create.sectionTitle', { entity: getEntity('session').singular })}
            </h2>
          </div>
        </Card.Header>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          <FormField label={t('sessions.create.titleLabel', { entity: getEntity('session').singular })} required error={errors.title}>
            <Input
              name="title"
              size="lg"
              value={formData.title}
              onChange={handleChange}
              leadingIcon={HiOutlineVideoCamera}
              placeholder={t('sessions.create.titlePlaceholder')}
              disabled={loading}
              autoFocus
            />
          </FormField>

          <FormField label={t('sessions.create.caseLabel', { caseEntity: getEntity('case').singular })} required error={errors.case}>
            <Select
              name="case"
              size="lg"
              value={formData.case}
              onChange={handleChange}
              disabled={loading || casesLoading}
            >
              <option value="">
                {casesLoading ? t('common.loading') : t('sessions.create.casePlaceholder', { caseEntity: getEntity('case').singular.toLowerCase() })}
              </option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </Select>
          </FormField>

          <FormField
            label={t('sessions.create.clientLabel', { clientEntity: getEntity('client').singular })}
            error={errors.client}
            help={t('sessions.create.clientHelp', { caseEntity: getEntity('case').singular.toLowerCase() })}
          >
            <Select
              name="client"
              size="lg"
              value={formData.client}
              onChange={handleChange}
              disabled={loading || clientsLoading || !formData.case}
            >
              <option value="">
                {clientsLoading ? t('common.loading') : t('sessions.create.clientPlaceholder', { clientEntity: getEntity('client').singular.toLowerCase() })}
              </option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label={t('sessions.create.dateLabel')} required error={errors.date}>
              <Input
                type="date"
                name="date"
                size="lg"
                value={formData.date}
                onChange={handleChange}
                leadingIcon={HiOutlineCalendarDays}
                disabled={loading}
              />
            </FormField>
            <FormField label={t('sessions.create.timeLabel')} required error={errors.time}>
              <Input
                type="time"
                name="time"
                size="lg"
                value={formData.time}
                onChange={handleChange}
                leadingIcon={HiOutlineClock}
                disabled={loading}
              />
            </FormField>
          </div>

          <FormField label={t('sessions.create.descriptionLabel')} error={errors.description} help={t('sessions.create.descriptionHelp')}>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder={t('sessions.create.descriptionPlaceholder', { entity: getEntity('session').singular.toLowerCase() })}
              disabled={loading}
            />
          </FormField>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 border-t border-border-subtle">
            <Button type="button" variant="secondary" onClick={() => navigate('/sessions')} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="lg"
              leadingIcon={loading ? null : HiOutlineCheckCircle}
              loading={loading}
            >
              {loading
                ? t('sessions.create.submitLoading', { entity: getEntity('session').singular.toLowerCase() })
                : t('sessions.create.submit', { entity: getEntity('session').singular.toLowerCase() })}
            </Button>
          </div>
        </form>
      </Card>

      <Banner tone="info" title={t('sessions.create.tipsTitle', { entity: getEntity('session').singular })}>
        <ul className="text-sm space-y-1 list-disc list-inside ms-1">
          <li>{t('sessions.create.tip1', { caseEntity: getEntity('case').singular.toLowerCase(), clientEntity: getEntity('client').singular.toLowerCase() })}</li>
          <li>{t('sessions.create.tip2')}</li>
        </ul>
      </Banner>
    </div>
  )
}
